import React, { memo, useState } from 'react'
import { RadioButton } from 'primereact/radiobutton'

import useText from '../../../../hooks/useText'
import { Dialog } from '../../../../uikit/Dialog'

export type LoadBalancerSetupGuideDialogProps = {
    visible: boolean
    onHide: () => void
    measurementDeviceId?: number | null
}

enum LoadBalancerSetupType {
    WithoutMeasurementDevice = '/common/content/images/Without_AG.png',
    ChargersAreIncludedInPowerConsumption = '/common/content/images/With_AG_included.png',
    ChargersAreNotIncludedInPowerConsumption = '/common/content/images/With_AG_included_charger_not_included.png',
}

const getDefaultSetupType = (measurementDeviceId?: number | null): LoadBalancerSetupType =>
    measurementDeviceId
        ? LoadBalancerSetupType.ChargersAreIncludedInPowerConsumption
        : LoadBalancerSetupType.WithoutMeasurementDevice

const setupOptions = [
    {
        id: 'without-measurement-device',
        label: 'btnFilterWithoutMeasurementDeviceLink',
        value: LoadBalancerSetupType.WithoutMeasurementDevice,
    },
    {
        id: 'chargers-included-in-measurement',
        label: 'btnFilterChargersIncludedInMNeasurementLink',
        value: LoadBalancerSetupType.ChargersAreIncludedInPowerConsumption,
    },
    {
        id: 'chargers-not-included-in-measurement',
        label: 'btnFilterChargersNotIncludedInMeasurementLink',
        value: LoadBalancerSetupType.ChargersAreNotIncludedInPowerConsumption,
    },
]

export const LoadBalancerSetupGuideDialog = memo(function LoadBalancerSetupGuideDialog({
    visible,
    onHide,
    measurementDeviceId,
}: LoadBalancerSetupGuideDialogProps): React.ReactElement {
    const text = useText()
    const [loadBalancerSetupType, setLoadBalancerSetupType] = useState<LoadBalancerSetupType>(() =>
        getDefaultSetupType(measurementDeviceId)
    )
    const selectedSetup = setupOptions.find(({ value }) => value === loadBalancerSetupType) ?? setupOptions[0]

    return (
        <Dialog
            header={text('lblLoadBalancerSetupGuideDialogTitle')}
            visible={visible}
            onHide={onHide}
            onShow={() => setLoadBalancerSetupType(getDefaultSetupType(measurementDeviceId))}
            style={{ width: '60rem' }}
        >
            <section className="flex flex-column lg:flex-row gap-4">
                <div
                    className="flex flex-column gap-3 flex-shrink-0"
                    role="radiogroup"
                    aria-label={text('lblLoadBalancerSetupGuideDialogTitle')}
                >
                    {setupOptions.map((option) => (
                        <div key={option.id} className="flex gap-2 align-items-center">
                            <RadioButton
                                inputId={option.id}
                                name="load-balancer-setup-type"
                                value={option.value}
                                className="w-1rem h-1rem"
                                onChange={(event) => setLoadBalancerSetupType(event.value)}
                                checked={loadBalancerSetupType === option.value}
                                pt={{
                                    box: { className: 'w-1rem h-1rem' },
                                    icon: { style: { width: '0.5rem', height: '0.5rem' } },
                                }}
                            />
                            <label htmlFor={option.id} className="cursor-pointer">
                                {text(option.label)}
                            </label>
                        </div>
                    ))}
                </div>
                <figure className="m-0 flex-1 text-center">
                    <img
                        src={selectedSetup.value}
                        alt={text(selectedSetup.label)}
                        width="500"
                        className="max-w-full h-auto"
                    />
                </figure>
            </section>
        </Dialog>
    )
})
